import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../api/client';
import { useBranches } from '../../hooks/useBranches';
import { useLines } from '../../hooks/useLines';
import { useMasters } from '../../hooks/useMasters';
import { usePermission } from '../../hooks/usePermission';
import { date, fine, money, today, weight } from '../../utils/format';
import { Chip, Empty, ErrorNotice, Loading, PageHeader, Table } from '../../components/ui';
import { DateInput, Field, NumberInput, Select, TextInput } from '../../components/ui/Form';
import { CustomerSelect } from '../pos/CustomerSelect';

/* ═══════════════════════════ shapes ══════════════════════════════════════ */

interface Item {
  description: string;
  metalId: string;
  declaredPurityId: string;
  testedPurityId: string;
  grossWt: string;
  stoneWt: string;
  otherWt: string;
  meltingLossPct: string;
  deductionMode: 'AMOUNT' | 'PCT';
  deduction: string;
  rateOverride: string;
}

interface QuoteLine {
  netWt: string;
  testedFineWt: string;
  declaredFineWt: string | null;
  meltingLossWt: string;
  payableFineWt: string;
  rate: string;
  rateIsOverride: boolean;
  grossPayable: string;
  deductionAmount: string;
  payableAmount: string;
}

interface Quote {
  lines: QuoteLine[];
  totals: {
    grossWt: string;
    netWt: string;
    payableFineWt: string;
    grossPayable: string;
    deductions: string;
    payable: string;
  };
}

interface Receipt {
  id: string;
  doc_no: string;
  doc_date: string;
  status: string;
  grand_total: string;
  testing_method: string | null;
  id_proof_type: string | null;
  id_proof_last4: string | null;
  customer_name: string;
  customer_phone: string | null;
  branch_name: string;
  metals: string | null;
  items: number;
  gross_wt: string;
  fine_wt: string;
  paid_by: string | null;
}

interface CustomerDetail {
  id: string;
  name: string;
  phone: string | null;
  pan: string | null;
  address: { line1?: string; line2?: string; city?: string; state?: string; pincode?: string } | null;
  invoice_count?: string;
}

const TESTING = [
  { value: 'TOUCHSTONE', label: 'Touchstone' },
  { value: 'XRF', label: 'XRF machine' },
  { value: 'CARAT_METER', label: 'Carat meter' },
  { value: 'FIRE_ASSAY', label: 'Fire assay' },
];

const ID_TYPES = [
  { value: 'Aadhaar', label: 'Aadhaar' },
  { value: 'PAN', label: 'PAN card' },
  { value: 'Voter ID', label: 'Voter ID' },
  { value: 'Driving licence', label: 'Driving licence' },
  { value: 'Passport', label: 'Passport' },
];

const PAY = [
  { value: 'CASH', label: 'Cash', hint: 'Paid from the till now.' },
  { value: 'BANK', label: 'Bank / UPI', hint: 'Paid into the customer’s account.' },
  {
    value: 'ADVANCE',
    label: 'Keep as advance',
    hint: 'Held for the customer to spend on a new piece — the till offers it as “Use advance”.',
  },
] as const;

const KINDS = ['Chain', 'Bangle', 'Ring', 'Necklace', 'Earrings', 'Coin', 'Anklet', 'Broken pieces'];

const n = (v: string | null | undefined): number => Number(v || 0);

/** Whole rupees, as a receipt states them. */
const rupees = (v: string | number | null | undefined): string =>
  `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ═══════════════════════════ the page ════════════════════════════════════ */

/**
 * Buying old gold — and old silver — from a customer.
 *
 * Every figure the customer will ask about is on the screen while the pieces
 * are being weighed: what they said it was, what the shop tested it at, the
 * weight less stones and dirt, the melting loss, the fine weight paid on, the
 * rate, and the money. The server works the figures out — the same engine the
 * receipt and the books use — so what is shown is exactly what will be paid.
 */
export function OldGoldPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'receipts' ? 'receipts' : 'new';

  return (
    <>
      <PageHeader
        title="Buy old gold"
        subtitle="Gold and silver bought from a customer — paid on the purity you test, not the purity they tell you."
        actions={
          <div className="flex overflow-hidden rounded-md border border-velvet">
            {(
              [
                ['new', 'New purchase'],
                ['receipts', 'Receipts'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                className={`px-4 py-1.5 text-sm font-medium ${tab === key ? 'bg-velvet text-white' : 'bg-surface text-ink-soft hover:bg-velvet-50'}`}
                onClick={() => setParams(key === 'new' ? {} : { tab: key }, { replace: true })}
              >
                {label}
              </button>
            ))}
          </div>
        }
      />
      {tab === 'new' ? <NewPurchase onDone={() => undefined} /> : <Receipts />}
    </>
  );
}

/* ═══════════════════════════ new purchase ════════════════════════════════ */

function NewPurchase({ onDone }: { onDone: () => void }) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const masters = useMasters();
  const { can } = usePermission();
  const { branches, defaultBranchId } = useBranches();
  const canRate = can('oldgold.approve');

  const gold = masters.metals.find((m) => m.code === 'GOLD');
  const blank = useMemo(
    () => (): Item => ({
      description: '',
      metalId: '',
      declaredPurityId: '',
      testedPurityId: '',
      grossWt: '',
      stoneWt: '',
      otherWt: '',
      meltingLossPct: '',
      deductionMode: 'AMOUNT',
      deduction: '',
      rateOverride: '',
    }),
    [],
  );
  const items = useLines<Item>(blank);

  const [customer, setCustomer] = useState<{ id: string; name: string } | null>(null);
  const [head, setHead] = useState({
    docDate: today(),
    branchId: '',
    testingMethod: 'XRF',
    idProofType: 'Aadhaar',
    idProofLast4: '',
    settlement: 'CASH' as 'CASH' | 'BANK' | 'ADVANCE',
  });
  const [error, setError] = useState<unknown>(null);
  const [approvalId, setApprovalId] = useState<string | null>(null);
  const [posted, setPosted] = useState<{
    purchaseId: string;
    docNo: string;
    payableAmount: string;
    totalFineWt: string;
  } | null>(null);
  const branchId = head.branchId || defaultBranchId || '';

  // Every new line starts on gold, the usual case, ready to weigh.
  const metalOf = (it: Item): string => it.metalId || gold?.id || '';

  const detail = useQuery({
    queryKey: ['customer', customer?.id],
    queryFn: () => api.get<CustomerDetail>(`/customers/${customer!.id}`),
    enabled: !!customer,
  });

  // The figures, worked out by the server as the pieces are weighed. Only
  // complete lines are sent; each answer is matched back to its line.
  const complete = items.lines
    .map((l, i) => ({ l, i }))
    .filter(({ l }) => metalOf(l) && l.testedPurityId && n(l.grossWt) > 0);
  const payload = complete.map(({ l }) => ({
    ...(l.description.trim() ? { description: l.description.trim() } : {}),
    metalId: metalOf(l),
    ...(l.declaredPurityId ? { declaredPurityId: l.declaredPurityId } : {}),
    testedPurityId: l.testedPurityId,
    grossWt: n(l.grossWt).toFixed(3),
    ...(n(l.stoneWt) > 0 ? { stoneWt: n(l.stoneWt).toFixed(3) } : {}),
    ...(n(l.otherWt) > 0 ? { otherWt: n(l.otherWt).toFixed(3) } : {}),
    ...(n(l.meltingLossPct) > 0 ? { meltingLossPct: n(l.meltingLossPct).toFixed(2) } : {}),
    ...(n(l.deduction) > 0 ? { deduction: { mode: l.deductionMode, value: n(l.deduction).toFixed(2) } } : {}),
    ...(canRate && n(l.rateOverride) > 0 ? { rateOverride: n(l.rateOverride).toFixed(2) } : {}),
  }));
  const quoteKey = JSON.stringify({ branchId, d: head.docDate, payload });
  const [debounced, setDebounced] = useState(quoteKey);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(quoteKey), 350);
    return () => clearTimeout(t);
  }, [quoteKey]);

  const quote = useQuery({
    queryKey: ['old-gold-quote', debounced],
    queryFn: () => {
      const q = JSON.parse(debounced) as { branchId: string; d: string; payload: unknown[] };
      return api.post<Quote>('/old-gold/quote', { branchId: q.branchId, docDate: q.d, lines: q.payload });
    },
    enabled: !!branchId && payload.length > 0 && debounced === quoteKey,
    placeholderData: (prev) => prev,
    retry: false,
  });
  const quoteFor = (index: number): QuoteLine | undefined => {
    const at = complete.findIndex((c) => c.i === index);
    return at >= 0 && quote.data && quote.data.lines.length === payload.length ? quote.data.lines[at] : undefined;
  };

  const incomplete = items.lines.length - complete.length;
  const total = quote.data && quote.data.lines.length === payload.length ? quote.data.totals : null;
  const ready = !!customer && !!branchId && complete.length > 0 && incomplete === 0 && !!total;

  const save = useMutation({
    mutationFn: () =>
      api.post<{ purchaseId: string; docNo: string; payableAmount: string; totalFineWt: string }>(
        '/old-gold',
        {
          branchId,
          docDate: head.docDate,
          customerId: customer!.id,
          testingMethod: head.testingMethod,
          ...(head.idProofType ? { idProofType: head.idProofType } : {}),
          ...(head.idProofLast4 ? { idProofLast4: head.idProofLast4 } : {}),
          settlement: head.settlement,
          ...(approvalId ? { approvalId } : {}),
          lines: payload,
        },
        { 'idempotency-key': crypto.randomUUID() },
      ),
    onSuccess: (r) => {
      setPosted(r);
      setError(null);
      setApprovalId(null);
      void client.invalidateQueries({ queryKey: ['old-gold'] });
      void client.invalidateQueries({ queryKey: ['dashboard'] });
      onDone();
    },
    onError: (e) => {
      // A cash payment over the limit goes to a manager; once they approve,
      // the same receipt is saved again with the approval attached.
      if (e instanceof ApiError && e.problem.code === 'WF_APPROVAL_REQUIRED') {
        const id = (e.problem.meta as { approvalRequestId?: string } | undefined)?.approvalRequestId;
        if (id) setApprovalId(id);
      }
      setError(e);
    },
  });

  const startAgain = (): void => {
    setPosted(null);
    setCustomer(null);
    items.reset();
    setHead((h) => ({ ...h, idProofLast4: '' }));
    setError(null);
    setApprovalId(null);
  };

  if (posted) {
    return (
      <div className="mx-auto max-w-xl rounded-lg border border-silver-100 bg-surface p-6 text-center shadow-card">
        <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-velvet-50 text-velvet">
          <svg viewBox="0 0 20 20" className="h-6 w-6" aria-hidden="true">
            <path
              d="M5 10.5l3.2 3L15 7"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <h2 className="text-lg font-semibold">Receipt {posted.docNo} saved</h2>
        <p className="mt-1 text-sm text-ink-soft">
          {fine(posted.totalFineWt)} fine bought from {customer?.name} for{' '}
          <span className="num font-semibold text-ink">{rupees(posted.payableAmount)}</span>
          {head.settlement === 'ADVANCE'
            ? ', kept as their advance to spend on a new piece.'
            : head.settlement === 'BANK'
              ? ', paid by bank.'
              : ', paid in cash.'}
        </p>
        <p className="mt-1 text-2xs text-ink-soft">The metal is in stock as old gold, ready to be melted.</p>
        <div className="mt-5 flex flex-wrap justify-center gap-2">
          <button className="btn-primary" onClick={() => navigate(`/purchases/${posted.purchaseId}/print`)}>
            Print receipt
          </button>
          {head.settlement === 'ADVANCE' ? (
            <Link className="btn-ghost" to="/pos">
              Make the new bill
            </Link>
          ) : null}
          <button className="btn-ghost" onClick={startAgain}>
            Buy from another customer
          </button>
        </div>
      </div>
    );
  }

  const cust = detail.data;
  const address = cust?.address
    ? [cust.address.line1, cust.address.city, cust.address.pincode].filter(Boolean).join(', ')
    : '';

  return (
    <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_22rem]">
      <div className="space-y-5">
        {/* Who is selling. */}
        <section className="rounded-lg border border-silver-100 bg-surface p-5 shadow-card">
          <h2 className="mb-3 text-base font-semibold">Customer</h2>
          <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_11rem]">
            <div>
              <span className="label">
                Who is selling <span className="text-danger">*</span>
              </span>
              <CustomerSelect
                value={customer}
                onChange={setCustomer}
                placeholder="Search by name or phone, or add a new customer"
              />
              {cust ? (
                <p className="mt-2 text-2xs text-ink-soft">
                  {[cust.phone, address, cust.pan ? `PAN ${cust.pan}` : null].filter(Boolean).join('  ·  ') ||
                    'No phone or address on record.'}
                </p>
              ) : (
                <p className="mt-2 text-2xs text-ink-faint">Old gold is never bought from someone unnamed.</p>
              )}
            </div>
            <Field label="Date" required>
              <DateInput value={head.docDate} onChange={(v) => setHead({ ...head, docDate: v })} />
            </Field>
          </div>

          <div className="mt-4 grid gap-4 border-t border-silver-100 pt-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="ID proof seen">
              <Select
                value={head.idProofType}
                onChange={(v) => setHead({ ...head, idProofType: v })}
                options={ID_TYPES}
                placeholder="None"
              />
            </Field>
            <Field label="Last 4 digits of the ID" hint="Only these four are kept.">
              <TextInput
                value={head.idProofLast4}
                onChange={(v) => setHead({ ...head, idProofLast4: v.replace(/\D/g, '').slice(0, 4) })}
                placeholder="1234"
                maxLength={4}
              />
            </Field>
            <Field label="Purity tested by" required>
              <Select
                value={head.testingMethod}
                onChange={(v) => setHead({ ...head, testingMethod: v })}
                options={TESTING}
              />
            </Field>
            {branches.length > 1 ? (
              <Field label="Branch" required>
                <Select
                  value={branchId}
                  onChange={(v) => setHead({ ...head, branchId: v })}
                  options={branches.map((b) => ({ value: b.id, label: b.name }))}
                />
              </Field>
            ) : null}
          </div>
        </section>

        {/* What they brought. */}
        <section className="rounded-lg border border-silver-100 bg-surface shadow-card">
          <div className="flex items-baseline justify-between border-b border-silver-100 px-5 py-4">
            <div>
              <h2 className="text-base font-semibold">Items brought in</h2>
              <p className="text-2xs text-ink-soft">Weigh each item; the payment works itself out as you type.</p>
            </div>
            <button type="button" className="btn-ghost" onClick={items.add}>
              Add another item
            </button>
          </div>

          <div className="divide-y divide-silver-100">
            {items.lines.map((line, index) => {
              const q = quoteFor(index);
              const metalId = metalOf(line);
              const purities = masters
                .puritiesFor(metalId)
                .map((p) => ({ value: p.id, label: `${p.code}${p.name ? ` — ${p.name}` : ''}` }));
              return (
                <div key={line.key} className="px-5 py-4">
                  <div className="mb-3 flex items-center gap-3">
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-velvet-50 text-2xs font-semibold text-velvet">
                      {index + 1}
                    </span>
                    <div className="flex flex-wrap gap-1">
                      {KINDS.map((k) => (
                        <button
                          key={k}
                          type="button"
                          className={`rounded-full border px-2.5 py-0.5 text-2xs ${
                            line.description === k
                              ? 'border-velvet bg-velvet text-white'
                              : 'border-silver-100 text-ink-soft hover:border-velvet-200'
                          }`}
                          onClick={() => items.update(line.key, { description: k })}
                        >
                          {k}
                        </button>
                      ))}
                    </div>
                    {items.lines.length > 1 ? (
                      <button
                        type="button"
                        className="btn-quiet ml-auto px-2 text-2xs text-danger"
                        onClick={() => items.remove(line.key)}
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <Field label="Description">
                      <TextInput
                        value={line.description}
                        onChange={(v) => items.update(line.key, { description: v })}
                        placeholder="2 bangles, one bent"
                      />
                    </Field>
                    <Field label="Metal" required>
                      <Select
                        value={metalId}
                        onChange={(v) =>
                          items.update(line.key, { metalId: v, declaredPurityId: '', testedPurityId: '' })
                        }
                        options={masters.activeMetals.map((m) => ({ value: m.id, label: m.name ?? m.code }))}
                      />
                    </Field>
                    <Field label="Customer says">
                      <Select
                        value={line.declaredPurityId}
                        onChange={(v) => items.update(line.key, { declaredPurityId: v })}
                        options={purities}
                        placeholder="Not said"
                      />
                    </Field>
                    <Field label="Tested at" required>
                      <Select
                        value={line.testedPurityId}
                        onChange={(v) => items.update(line.key, { testedPurityId: v })}
                        options={purities}
                      />
                    </Field>
                    <Field label="Gross weight" required>
                      <NumberInput
                        value={line.grossWt}
                        onChange={(v) => items.update(line.key, { grossWt: v })}
                        decimals={3}
                        suffix="g"
                        placeholder="0.000"
                      />
                    </Field>
                    <Field label="Less stones">
                      <NumberInput
                        value={line.stoneWt}
                        onChange={(v) => items.update(line.key, { stoneWt: v })}
                        decimals={3}
                        suffix="g"
                        placeholder="0.000"
                      />
                    </Field>
                    <Field label="Less dirt, lac, wax">
                      <NumberInput
                        value={line.otherWt}
                        onChange={(v) => items.update(line.key, { otherWt: v })}
                        decimals={3}
                        suffix="g"
                        placeholder="0.000"
                      />
                    </Field>
                    <Field label="Melting loss">
                      <NumberInput
                        value={line.meltingLossPct}
                        onChange={(v) => items.update(line.key, { meltingLossPct: v })}
                        decimals={2}
                        max={100}
                        suffix="%"
                        placeholder="0.00"
                      />
                    </Field>
                    <Field label="Other deduction">
                      <div className="flex gap-1">
                        <div className="flex-1">
                          <NumberInput
                            value={line.deduction}
                            onChange={(v) => items.update(line.key, { deduction: v })}
                            decimals={2}
                            placeholder="0.00"
                          />
                        </div>
                        <select
                          className="field w-16"
                          value={line.deductionMode}
                          onChange={(e) =>
                            items.update(line.key, { deductionMode: e.target.value as 'AMOUNT' | 'PCT' })
                          }
                          aria-label="Deduction in rupees or percent"
                        >
                          <option value="AMOUNT">₹</option>
                          <option value="PCT">%</option>
                        </select>
                      </div>
                    </Field>
                    <Field
                      label="Rate per fine gram"
                      hint={canRate ? 'Blank uses today’s purchase rate.' : 'Today’s purchase rate.'}
                    >
                      {canRate ? (
                        <NumberInput
                          value={line.rateOverride}
                          onChange={(v) => items.update(line.key, { rateOverride: v })}
                          decimals={2}
                          suffix="₹"
                          placeholder={q && !q.rateIsOverride ? n(q.rate).toFixed(2) : 'Today’s rate'}
                        />
                      ) : (
                        <p className="field num bg-silver-50">{q ? rupees(q.rate) : '—'}</p>
                      )}
                    </Field>
                  </div>

                  {/* The working, in the order the customer is told it. */}
                  <div className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-md bg-velvet-50 px-3 py-2 text-sm">
                    {q ? (
                      <>
                        <Step label="Net weight" value={weight(q.netWt)} />
                        <Step label="Fine at tested purity" value={fine(q.testedFineWt)} />
                        {q.declaredFineWt && q.declaredFineWt !== q.testedFineWt ? (
                          <Step label="they expected" value={fine(q.declaredFineWt)} muted />
                        ) : null}
                        {n(q.meltingLossWt) > 0 ? (
                          <Step label="less melting loss" value={fine(q.meltingLossWt)} />
                        ) : null}
                        <Step label="Paid on" value={fine(q.payableFineWt)} strong />
                        <span className="text-ink-faint">×</span>
                        <Step label="rate" value={rupees(q.rate)} />
                        {n(q.deductionAmount) > 0 ? (
                          <Step label="less deduction" value={rupees(q.deductionAmount)} />
                        ) : null}
                        <span className="ml-auto text-right">
                          <span className="block text-2xs text-ink-soft">Amount</span>
                          <span className="num text-lg font-semibold text-velvet">{rupees(q.payableAmount)}</span>
                        </span>
                      </>
                    ) : (
                      <span className="text-2xs text-ink-soft">
                        {!line.testedPurityId || n(line.grossWt) === 0
                          ? 'Choose the tested purity and enter the gross weight to see the amount.'
                          : quote.isFetching
                            ? 'Working it out…'
                            : 'Waiting for the figures…'}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* What is paid, and how. */}
      <aside className="space-y-4 lg:sticky lg:top-28">
        <section className="overflow-hidden rounded-lg bg-rail text-rail-ink shadow-card">
          <div className="h-1 bg-gradient-to-r from-gold-500 via-gold-300 to-gold-500" />
          <div className="p-5">
            <p className="text-sm text-rail-ink/75">To pay {customer ? customer.name : 'the customer'}</p>
            <p className="num mt-1 text-4xl font-semibold tracking-tight">{rupees(total?.payable ?? 0)}</p>
            <dl className="mt-4 space-y-1 text-sm">
              <SumRow label={`Items`} value={String(items.lines.length)} />
              <SumRow label="Gross weight" value={weight(total?.grossWt ?? '0')} />
              <SumRow label="Net weight" value={weight(total?.netWt ?? '0')} />
              <SumRow label="Fine weight paid on" value={fine(total?.payableFineWt ?? '0')} strong />
              <SumRow label="Value" value={rupees(total?.grossPayable ?? 0)} />
              {n(total?.deductions) > 0 ? <SumRow label="Less deductions" value={rupees(total?.deductions)} /> : null}
            </dl>
          </div>
        </section>

        <section className="rounded-lg border border-silver-100 bg-surface p-5 shadow-card">
          <p className="label">Pay by</p>
          <div className="grid gap-2">
            {PAY.map((p) => (
              <label
                key={p.value}
                className={`flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2 ${
                  head.settlement === p.value
                    ? 'border-velvet bg-velvet-50'
                    : 'border-silver-100 hover:border-velvet-200'
                }`}
              >
                <input
                  type="radio"
                  className="mt-1"
                  checked={head.settlement === p.value}
                  onChange={() => setHead({ ...head, settlement: p.value })}
                />
                <span>
                  <span className="block text-sm font-medium">{p.label}</span>
                  <span className="block text-2xs text-ink-soft">{p.hint}</span>
                </span>
              </label>
            ))}
          </div>

          <ErrorNotice error={error ?? (quote.error && payload.length > 0 ? quote.error : null)} />
          {approvalId ? (
            <p className="mt-2 rounded-md bg-gold-50 px-3 py-2 text-2xs text-gold-700">
              Sent for approval. Once a manager approves it under Approvals, press the button again — the same receipt
              is saved with the approval.
            </p>
          ) : null}

          <button
            className="btn-primary mt-4 w-full py-2.5 text-base"
            disabled={!ready || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending
              ? 'Saving…'
              : head.settlement === 'ADVANCE'
                ? `Keep ${rupees(total?.payable ?? 0)} as their advance`
                : `Pay ${rupees(total?.payable ?? 0)} and save`}
          </button>
          {!customer ? (
            <p className="mt-2 text-center text-2xs text-ink-soft">Choose the customer first.</p>
          ) : incomplete > 0 ? (
            <p className="mt-2 text-center text-2xs text-ink-soft">
              {incomplete === 1 ? 'One item still needs' : `${incomplete} items still need`} a tested purity and weight.
            </p>
          ) : null}
        </section>
      </aside>
    </div>
  );
}

function Step({ label, value, strong, muted }: { label: string; value: string; strong?: boolean; muted?: boolean }) {
  return (
    <span className={muted ? 'text-ink-faint' : ''}>
      <span className="block text-2xs text-ink-soft">{label}</span>
      <span className={`num ${strong ? 'font-semibold' : ''}`}>{value}</span>
    </span>
  );
}

function SumRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={`flex justify-between ${strong ? 'font-semibold' : ''}`}>
      <dt className="text-rail-ink/75">{label}</dt>
      <dd className="num">{value}</dd>
    </div>
  );
}

/* ═══════════════════════════ receipts ════════════════════════════════════ */

function Receipts() {
  const navigate = useNavigate();
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(today());
  const q = useQuery({
    queryKey: ['old-gold', from, to],
    queryFn: () => api.get<{ data: Receipt[] }>(`/old-gold?from=${from}&to=${to}&limit=200`),
  });
  const rows = q.data?.data ?? [];
  const posted = rows.filter((r) => r.status === 'POSTED');

  return (
    <>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <p className="label">From</p>
          <DateInput value={from} onChange={setFrom} />
        </div>
        <div>
          <p className="label">To</p>
          <DateInput value={to} onChange={setTo} />
        </div>
        {posted.length > 0 ? (
          <p className="ml-auto text-sm text-ink-soft">
            {posted.length} receipt{posted.length === 1 ? '' : 's'},{' '}
            <span className="num font-semibold text-ink">
              {fine(posted.reduce((a, r) => a + n(r.fine_wt), 0).toFixed(4))}
            </span>{' '}
            fine,{' '}
            <span className="num font-semibold text-ink">
              {money(posted.reduce((a, r) => a + n(r.grand_total), 0).toFixed(2))}
            </span>{' '}
            paid
          </p>
        ) : null}
      </div>
      <ErrorNotice error={q.error} />
      {q.isLoading ? <Loading /> : null}
      {!q.isLoading && rows.length === 0 ? (
        <Empty message="No old gold bought in these dates." hint="Receipts appear here as soon as they are saved." />
      ) : null}
      {rows.length > 0 ? (
        <Table
          head={[
            'Date',
            'Receipt',
            'Customer',
            'Metal',
            'Items',
            'Gross',
            'Fine paid on',
            'Amount',
            'Paid by',
            'ID',
            '',
          ]}
        >
          {rows.map((r) => (
            <tr key={r.id} className={r.status === 'CANCELLED' ? 'text-ink-faint line-through' : ''}>
              <td className="td">{date(r.doc_date)}</td>
              <td className="td num">{r.doc_no}</td>
              <td className="td">
                {r.customer_name}
                {r.customer_phone ? <span className="block text-2xs text-ink-soft">{r.customer_phone}</span> : null}
              </td>
              <td className="td">{r.metals ?? '—'}</td>
              <td className="td num text-right">{r.items}</td>
              <td className="td num text-right">{weight(r.gross_wt)}</td>
              <td className="td num text-right">{fine(r.fine_wt)}</td>
              <td className="td num text-right font-medium">{money(r.grand_total)}</td>
              <td className="td">
                <Chip tone="mute">
                  {r.paid_by === 'BANK'
                    ? 'Bank'
                    : r.paid_by === 'KEPT_AS_ADVANCE'
                      ? 'Advance'
                      : r.paid_by === 'AGAINST_SALE'
                        ? 'Credit'
                        : 'Cash'}
                </Chip>
              </td>
              <td className="td text-2xs">
                {r.id_proof_type ? `${r.id_proof_type} ${r.id_proof_last4 ? `…${r.id_proof_last4}` : ''}` : '—'}
              </td>
              <td className="td text-right">
                <button className="btn-ghost px-2 py-1 text-xs" onClick={() => navigate(`/purchases/${r.id}/print`)}>
                  Print
                </button>
              </td>
            </tr>
          ))}
        </Table>
      ) : null}
    </>
  );
}
